/**
 * 账号开通财务统计 API 服务
 */

const API_BASE_URL = '/api';

/**
 * 每日统计
 */
export interface DailyStat {
  id: number;
  stat_date: string;
  total_accounts: number;
  total_tokens: number;
  total_vip_days: number;
  success_count: number;
  failed_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * 统计汇总
 */
export interface StatSummary {
  total_accounts: number;
  total_tokens: number;
  total_vip_days: number;
  success_count: number;
  failed_count: number;
}

/**
 * 统计汇总响应
 */
export interface StatsSummaryResponse {
  today: StatSummary;
  monthly: StatSummary;
  yearly: StatSummary;
  trend: DailyStat[];
}

/**
 * 统计查询参数
 */
export interface StatsQueryParams {
  start_date: string;
  end_date: string;
}

/**
 * 统计响应（按日期范围）
 */
export interface StatsResponse {
  items: DailyStat[];
  summary: StatSummary;
}

/**
 * API响应
 */
interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * 获取认证token
 */
function getAuthHeaders(): HeadersInit {
  const authStore = localStorage.getItem('auth-storage');
  if (authStore) {
    const { state } = JSON.parse(authStore);
    const token = state?.accessToken;
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
  }
  return {
    'Content-Type': 'application/json',
  };
}

/**
 * 账号开通财务统计 API 服务
 */
export class AccountActivationStatsApiService {
  private readonly baseUrl = `${API_BASE_URL}/admin/account-activations`;

  /**
   * 获取统计汇总（今日、本月、本年）
   */
  async getStatsSummary(): Promise<StatsSummaryResponse> {
    const response = await fetch(`${this.baseUrl}/stats/summary`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || '获取统计汇总失败');
    }

    const result: ApiResponse<StatsSummaryResponse> = await response.json();
    if (!result.success) {
      throw new Error(result.message || result.error || '获取统计汇总失败');
    }

    return result.data!;
  }

  /**
   * 获取财务统计数据（按日期范围）
   */
  async getActivationStats(params: StatsQueryParams): Promise<StatsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('start_date', params.start_date);
    queryParams.append('end_date', params.end_date);

    const response = await fetch(`${this.baseUrl}/stats?${queryParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || '获取财务统计数据失败');
    }

    const result: ApiResponse<StatsResponse> = await response.json();
    if (!result.success) {
      throw new Error(result.message || result.error || '获取财务统计数据失败');
    }

    return result.data!;
  }

  /**
   * 导出Excel（下载CSV文件）
   */
  async exportStatsExcel(params: StatsQueryParams): Promise<void> {
    const queryParams = new URLSearchParams();
    queryParams.append('start_date', params.start_date);
    queryParams.append('end_date', params.end_date);

    const response = await fetch(`${this.baseUrl}/stats/export?${queryParams}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || error.error || '导出失败');
    }

    // 获取文件名
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `account_activation_stats_${params.start_date}_${params.end_date}.csv`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, '');
      }
    }

    // 创建下载链接
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}

// 导出单例
export const accountActivationStatsApi = new AccountActivationStatsApiService();
